import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, UIEvent } from "react";

export type Column<T> = {
  key: string;
  label: string;
  render?: (row: T, index?: number) => React.ReactNode;
  isSortable?: boolean;
  sort?: "asc" | "desc" | null;
  cellClassName?: string;
  headerClassName?: string;
  cell?: (params: { row: { original: T } }) => React.ReactNode;
};

type PaginatedData<T> = {
  data: T[];
  total: number;
};

type DataTableProps<T> = {
  data: PaginatedData<T>;
  columns: Column<T>[];
  rowsPerPage?: number;
  setPageChange?: (page: number) => void;
  callSort?: (sort: { key: keyof T | null; sort: string | null }) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMoreData?: boolean;
  allData?: T[];
  onRowClick?: (row: T) => void;
  onScroll?: (scrollInfo: {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => void;
  height?: string | number;
  maxHeight?: string | number;
  skeletonCount?: number;
  showEndMessage?: boolean;
};

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  rowsPerPage = 10,
  setPageChange,
  callSort,
  isLoading = false,
  isLoadingMore = false,
  hasMoreData = true,
  allData,
  onRowClick,
  onScroll,
  height = "100%",
  maxHeight,
  skeletonCount,
  showEndMessage = false,
}: DataTableProps<T>) {
  const [currentPage] = useState(1);
  const [tableColumns, setTableColumns] = useState<Column<T>[]>(columns);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const displayData = allData || data?.data || [];

  useEffect(() => {
    if (setPageChange) {
      setPageChange(currentPage);
    }
  }, [currentPage, setPageChange]);

  useEffect(() => {
    setTableColumns(columns);
  }, [columns]);

  const handleColumnSort = (sort: string) => {
    const getNextSort = (
      currentSort: "asc" | "desc" | null | undefined
    ): "asc" | "desc" | null => {
      if (currentSort === "asc") return "desc";
      if (currentSort === "desc") return null;
      return "asc";
    };

    const newColumns = tableColumns.map((col) => {
      if (col.key === sort) {
        const updated: Column<T> = {
          ...col,
          sort: getNextSort(col.sort),
        };
        if (callSort) {
          if (updated.sort) {
            callSort({ key: updated.key as keyof T, sort: updated.sort });
          } else {
            callSort({ key: null, sort: null });
          }
        }
        return updated;
      }
      return { ...col, sort: null };
    });

    setTableColumns(newColumns);
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (onScroll) {
        onScroll({ scrollTop, scrollHeight, clientHeight });
      }
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const renderSkeletonRows = (count: number) => {
    const skeletonRows = Array.from({ length: count }, () => ({
      key: `skeleton-row-${crypto.randomUUID()}`,
    }));

    return skeletonRows.map((row) => (
      <tr key={row.key} style={{ height: "40px" }}>
        {tableColumns.map((col) => (
          <td
            key={`skeleton-cell-${col.key}`}
            className={cn("py-[10px] px-4 align-middle", col.cellClassName)}>
            <Skeleton className='h-4 w-full bg-primary/10 dark:bg-gray-700' />
          </td>
        ))}
      </tr>
    ));
  };

  const renderLoadingMoreIndicator = () => {
    if (!isLoadingMore) return null;
    return renderSkeletonRows(skeletonCount ?? rowsPerPage);
  };

  const renderNoDataMessage = () => {
    if (isLoading || displayData.length > 0) return null;

    return (
      <tr>
        <td
          colSpan={tableColumns.length}
          className='p-0 bg-white dark:bg-[#061A23]' // ✅ ensures single background
        >
          <div
            className='flex flex-col items-center justify-center text-center w-full'
            style={{
              height: "390px", // ✅ FULL empty height
              fontFamily: "Inter",
              overflow: "hidden",
            }}>
            <div className='w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-2'>
              <svg
                className='w-8 h-8 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
            </div>

            <p className='text-lg font-medium text-grey-600 dark:text-white'>
              No records found
            </p>

            <p className='text-sm text-grey-600 dark:text-gray-400'>
              Try adjusting your filters or search criteria
            </p>
          </div>
        </td>
      </tr>
    );
  };

  const renderEndOfDataMessage = () => {
    if (isLoadingMore || isLoading || displayData.length === 0 || hasMoreData)
      return null;

    return (
      <tr style={{ height: "40px" }}>
        <td colSpan={tableColumns.length} className='text-center py-[10px]'>
          <div className='flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400'>
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #BFBFBF;
          border-radius: 10px;
          transition: background 0.2s ease;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #999999;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #737373;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4D4D4D;
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #BFBFBF transparent;
        }

        .dark .custom-scrollbar {
          scrollbar-color: #737373 transparent;
        }
      `}</style>
      <div
        ref={containerRef}
        className='custom-scrollbar relative overflow-auto rounded-[6px] border border-[#E5E5E5] dark:border-[#4C4C4C]'
        onScroll={handleScroll}
        style={{
          height: height,
          maxHeight: maxHeight || height, // Use maxHeight if provided, otherwise fall back to height
          position: "relative",
          isolation: "isolate",
        }}>
        <table className='w-full table-auto bg-white dark:bg-[#061A23]'>
          <thead className='sticky top-0 z-20'>
            <tr>
              {tableColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "whitespace-nowrap overflow-hidden bg-grey-200 dark:bg-[#202832] border-b border-[#E5E5E5] dark:border-[#4C4C4C] text-grey-800 dark:text-white ",
                    col.headerClassName,
                    col.cellClassName
                  )}
                  style={{
                    minHeight: "40px",
                    maxHeight: "40px",
                    height: "40px",
                    paddingTop: "10px",
                    paddingRight: "16px",
                    paddingBottom: "10px",
                    paddingLeft: "16px",
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0px",
                    textAlign: "left",
                    boxSizing: "border-box",
                    borderBottom: "none",
                  }}
                  onClick={() => {
                    if (col.isSortable) handleColumnSort(col.key);
                  }}>
                  <div className='flex items-center justify-start space-x-2'>
                    <span>{col.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='bg-white dark:bg-[#061A23]'>
            {isLoading &&
              displayData.length === 0 &&
              renderSkeletonRows(skeletonCount ?? rowsPerPage)}

            {displayData?.map((row: T, index: number) => (
              <tr
                key={row.id}
                className={cn(
                  "bg-white dark:bg-[#061A23] hover:bg-none dark:hover:bg-none border-b border-[#E5E5E5] dark:border-[#4C4C4C] last:border-b-0",
                  onRowClick && "cursor-pointer"
                )}
                style={{ height: "40px" }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {tableColumns?.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "align-middle text-grey-800 dark:text-white",
                      col.cellClassName
                    )}
                    style={{
                      minHeight: "40px",
                      maxHeight: "40px",
                      height: "40px",
                      paddingTop: "10px",
                      paddingRight: "16px",
                      paddingBottom: "10px",
                      paddingLeft: "16px",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: "0px",
                      textAlign: "left",
                      boxSizing: "border-box",
                    }}>
                    {col.render
                      ? col.render(row, index)
                      : (row[col.key as keyof T] as string)}
                  </td>
                ))}
              </tr>
            ))}

            {renderLoadingMoreIndicator()}
            {renderNoDataMessage()}
            {showEndMessage && renderEndOfDataMessage()}
          </tbody>
        </table>
      </div>
    </>
  );
}
